package co.maytes.standupbot.dashboard.features.standupruns.tabs

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.prompts.PromptDto
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.px

@Composable
internal fun SystemPromptBlock(runPromptVersion: String?) {
    val api = rememberApiClient()
    val auth = rememberAuthStore()
    val workspaceId = auth.currentUser?.workspace?.id
    var prompt by remember { mutableStateOf<PromptDto?>(null) }
    var loading by remember { mutableStateOf(false) }
    var loadError by remember { mutableStateOf<String?>(null) }
    var expanded by remember { mutableStateOf(false) }

    LaunchedEffect(workspaceId) {
        val ws = workspaceId ?: return@LaunchedEffect
        loading = true
        when (val res = api.get<PromptDto>("/api/v1/workspaces/$ws/prompts/user-synthesis")) {
            is Result.Ok -> {
                prompt = res.value
                loadError = null
            }
            is Result.Err -> {
                loadError = res.error.displayMessage
            }
        }
        loading = false
    }

    val toggleLabel = buildPromptToggleLabel(
        expanded = expanded,
        loading = loading,
        runPromptVersion = runPromptVersion,
        fetchedVersion = prompt?.version,
    )

    Column(modifier = Modifier.fillMaxWidth().margin(top = Spacing.lg)) {
        SpanText(
            text = "System prompt",
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .fontWeight(600)
                .color(Palette.textSecondary),
        )
        Box(
            modifier = Modifier
                .margin(top = Spacing.xs)
                .padding(topBottom = 4.px, leftRight = Spacing.sm)
                .borderRadius(Radius.full)
                .backgroundColor(Palette.surfaceSubtle)
                .color(Palette.accent)
                .cursor(Cursor.Pointer)
                .onClick {
                    if (prompt != null) expanded = !expanded
                },
        ) {
            SpanText(
                text = toggleLabel,
                modifier = Modifier.fontSize(Typography.caption.size),
            )
        }
        loadError?.let { err ->
            SpanText(
                text = err,
                modifier = Modifier
                    .margin(top = Spacing.xs)
                    .fontSize(Typography.caption.size)
                    .color(Palette.error),
            )
        }
        if (expanded && prompt != null) {
            val mismatch = runPromptVersion != null && runPromptVersion != prompt!!.version
            if (mismatch) {
                SpanText(
                    text = "This run used v$runPromptVersion. The prompt below is the current version (v${prompt!!.version}).",
                    modifier = Modifier
                        .margin(top = Spacing.xs)
                        .fontSize(Typography.caption.size)
                        .color(Palette.warning),
                )
            }
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .margin(top = Spacing.xs)
                    .padding(Spacing.md)
                    .borderRadius(Radius.md)
                    .backgroundColor(Palette.surfaceSubtle)
                    .styleModifier {
                        property("font-family", Typography.FONT_MONO)
                        property("font-size", "12px")
                        property("white-space", "pre-wrap")
                        property("overflow-wrap", "anywhere")
                        property("max-height", "400px")
                        property("overflow-y", "auto")
                        property("color", "rgb(9, 9, 11)")
                    },
            ) {
                SpanText(text = prompt!!.system)
            }
        }
    }
}

private fun buildPromptToggleLabel(
    expanded: Boolean,
    loading: Boolean,
    runPromptVersion: String?,
    fetchedVersion: String?,
): String {
    if (loading && fetchedVersion == null) return "Loading prompt…"
    if (fetchedVersion == null) return "Prompt unavailable"
    val verb = if (expanded) "Hide prompt" else "View prompt"
    return when {
        runPromptVersion == null -> "$verb (v$fetchedVersion)"
        runPromptVersion == fetchedVersion -> "$verb (v$fetchedVersion)"
        else -> "$verb — run was v$runPromptVersion, current is v$fetchedVersion"
    }
}
